/**
 * `<DoctorObservations>` — in-call free-text scratchpad for the doctor.
 *
 * Mounted in the right column of `<ZoomPanel>` above the transcript stream.
 * The doctor types observations the AI scribe should consider when drafting
 * the SOAP note — e.g. "patient denies all opioid use, family history of
 * hemochromatosis", "monitor LFTs in 4 weeks", or general clinical
 * impressions that won't necessarily be voiced during the call.
 *
 * Persistence
 *  - State is mirrored to `localStorage` keyed on the FHIR `encounterId` so a
 *    page reload mid-call — OR an interrupted-visit resume that adopts a new
 *    server scribe-session id — doesn't drop the doctor's notes. (Keying on
 *    the volatile `scribeSessionId` used to lose the scratchpad whenever a
 *    resume swapped the session id.) A back-compat read against the old
 *    session-id key is kept so notes written before this change still hydrate.
 *  - On End Session (`useFinalizeScribe`), the text is forwarded to the
 *    `/generate-draft` BE endpoint as a tagged "DOCTOR'S PRIVATE
 *    OBSERVATIONS" block in the user prompt. The system prompt weights
 *    these heavily for the SOAP Assessment + Plan.
 *  - We DO NOT persist the text as a FHIR resource — the AI's resulting
 *    SOAP note IS the audit trail; raw observations are a draft.
 *
 * Collapse behavior
 *  - Default-collapsed — one slim header line; the transcript keeps the
 *    space on calls where the doctor never types. When collapsed with
 *    content, a dim truncated preview shows something is inside.
 *  - Collapsed state is per-session (in-memory) so the doctor can hide
 *    the field briefly without losing their place across the call.
 */
import { useEffect } from 'react'
import { ActionIcon, Box, Group, Text, Textarea } from '@mantine/core'
import { IconChevronDown, IconChevronUp, IconSparkles } from '@tabler/icons-react'
import { buildObservationsStorageKey } from './observationsStorage'

export interface DoctorObservationsProps {
  /** Current FHIR encounter id; drives the localStorage key. Null when no
   *  encounter context is available (the field is still editable but won't
   *  persist). Stable across an interrupted-visit resume. */
  encounterId: string | null | undefined
  /** Current text value. Controlled by the parent so the AI-finalize hook
   *  can pull it from the same source of truth on End Session. */
  value: string
  /** Setter for the parent's controlled state. */
  onChange: (next: string) => void
  /** Collapsed UI state — local to the call session, not persisted. */
  collapsed: boolean
  onToggleCollapsed: () => void
}

export function DoctorObservations({
  encounterId,
  value,
  onChange,
  collapsed,
  onToggleCollapsed,
}: DoctorObservationsProps) {
  // Mirror text to localStorage whenever it changes, scoped to the encounter.
  // Cheap because Textarea fires `onChange` per keystroke and the writes
  // are tiny; React 18 batching keeps this from running per-letter on slow
  // typists.
  useEffect(() => {
    const key = buildObservationsStorageKey(encounterId)
    if (!key || typeof window === 'undefined') return
    try {
      window.localStorage.setItem(key, value)
    } catch {
      // Private mode / sandbox — in-memory React state still applies.
    }
  }, [encounterId, value])

  return (
    <Box
      data-testid="zoom-panel-doctor-observations"
      className="shrink-0 border-b border-white/[0.07]"
    >
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        className="cursor-pointer px-[14px] py-[6px] select-none"
        onClick={onToggleCollapsed}
      >
        <Group gap={6} align="center" wrap="nowrap" className="min-w-0">
          <IconSparkles size={11} style={{ color: '#a5b4fc' }} aria-hidden="true" />
          <Text fz="10.5px" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
            Add to Progress Note
          </Text>
          {collapsed && value.trim() ? (
            <Text size="xs" c="dimmed" truncate className="min-w-0 italic">
              {value.trim()}
            </Text>
          ) : null}
        </Group>
        <ActionIcon
          variant="subtle"
          size="xs"
          color="gray"
          aria-label={collapsed ? 'Expand observations' : 'Collapse observations'}
          onClick={(e) => {
            e.stopPropagation()
            onToggleCollapsed()
          }}
        >
          {collapsed ? <IconChevronDown size={14} /> : <IconChevronUp size={14} />}
        </ActionIcon>
      </Group>

      {!collapsed && (
        <Box className="px-[14px] pb-[8px]">
          <Textarea
            data-testid="zoom-panel-doctor-observations-textarea"
            placeholder="Add notes, clinical impressions, follow-up instructions, or treatment rationale to include in the Progress Note."
            value={value}
            onChange={(event) => onChange(event.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={10}
            styles={{
              input: {
                background: '#0b1220',
                border: '1px solid #1e293b',
                color: '#e2e8f0',
                fontSize: 12,
                lineHeight: 1.5,
              },
            }}
          />
        </Box>
      )}
    </Box>
  )
}
